
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';
import LinkTokenCreate from 'src/components/docs/LinkTokenCreate';
export const metadata = {
  toc: true,
  layout: 'guide',
  description: 'Learn how to migrate from Asset Reports to Base Reports'
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <Header title='Migrate from Asset Reports to Base Reports' subtitle='Migrate to Base Reports to unlock access to the Consumer Report' mdxType="Header" />
    <p>{`This guide will walk you through how to migrate from generating Asset Reports with Plaid's `}<a parentName="p" {...{
        "href": "https://plaid.com/products/assets/"
      }}>{`Assets`}</a>{` product to generating Base Reports with Plaid Check's `}<a parentName="p" {...{
        "href": "https://plaid.com/check/income-and-underwriting/"
      }}>{`Consumer Report`}</a>{` product.`}</p>
    <h4 {...{
      "id": "prerequisites"
    }}>{`Prerequisites`}</h4>
    <ol>
      <li parentName="ol">{`To use Consumer Report, it is strongly recommended to update your Plaid client library to the latest version. The minimum required versions for new Consumer Report integrations are:`}
        <ul parentName="li">
          <li parentName="ul">{`Python: 38.0.0`}</li>
          <li parentName="ul">{`Go: 41.0.0`}</li>
          <li parentName="ul">{`Java: 39.0.0`}</li>
          <li parentName="ul">{`Node: 41.0.0`}</li>
          <li parentName="ul">{`Ruby: 45.0.0`}</li>
        </ul>
      </li>
      <li parentName="ol"><a parentName="li" {...{
          "href": "https://dashboard.plaid.com/settings/team/products"
        }}>{`Confirm`}</a>{` that you have access to all required Plaid Check products in the Production environment. If you don't have access to Plaid Check, `}<a parentName="li" {...{
          "href": "https://dashboard.plaid.com/overview/request-products"
        }}>{`request access via the Dashboard`}</a>{`. In order to migrate from Assets to Plaid Consumer Report, your end users must be in the US.`}</li>
    </ol>
    <h4 {...{
      "id": "changes-to-plaid-link-initialization"
    }}>{`Changes to Plaid Link initialization`}</h4>
    <p>{`When using Plaid Check products, you must create a user prior to sending the user through Link, and you must initialize Link with the resulting `}<inlineCode parentName="p">{`user_id`}</inlineCode>{`. This allows Plaid to associate multiple Items with a single user.`}</p>
    <ol>
      <li parentName="ol">{`Call `}<inlineCode parentName="li">{`/user/create`}</inlineCode>{` prior to creating a Link token`}
        <ul parentName="li">
          <li parentName="ul">{`Include information about your user in the `}<inlineCode parentName="li">{`identity`}</inlineCode>{` object. At minimum, the following fields must be provided and non-empty: `}<inlineCode parentName="li">{`name`}</inlineCode>{`, `}<inlineCode parentName="li">{`date_of_birth`}</inlineCode>{`, `}<inlineCode parentName="li">{`emails`}</inlineCode>{`, `}<inlineCode parentName="li">{`phone_numbers`}</inlineCode>{`, and `}<inlineCode parentName="li">{`addresses`}</inlineCode>{` (with at least one email address, phone number, and address designated as `}<inlineCode parentName="li">{`primary`}</inlineCode>{`). If you intend to share the report with a GSE (Government-Sponsored Enterprise) such as Fannie or Freddie, the full SSN is also required via the `}<inlineCode parentName="li">{`id_numbers`}</inlineCode>{` field. For all use cases, providing at least a partial SSN is highly recommended, since it improves the accuracy of matching user records during compliance processes such as file disclosure, dispute, or security freeze requests.`}</li>
          <li parentName="ul">{`Store the `}<inlineCode parentName="li">{`user_id`}</inlineCode>{` in your database`}</li>
        </ul>
      </li>
      <li parentName="ol">{`Update your call to `}<inlineCode parentName="li">{`/link/token/create`}</inlineCode>
        <ul parentName="li">
          <li parentName="ul">{`Include the `}<inlineCode parentName="li">{`user_id`}</inlineCode>{` string from `}<inlineCode parentName="li">{`/user/create`}</inlineCode></li>
          <li parentName="ul">{`Replace the `}<inlineCode parentName="li">{`assets`}</inlineCode>{` product string with `}<inlineCode parentName="li">{`cra_base_report`}</inlineCode>{` in the `}<inlineCode parentName="li">{`products`}</inlineCode>{` array`}</li>
          <li parentName="ul">{`Add a `}<inlineCode parentName="li">{`cra_options`}</inlineCode>{` object and specify your desired `}<inlineCode parentName="li">{`days_requested`}</inlineCode>{` (minimum 180)`}</li>
          <li parentName="ul">{`Include a `}<inlineCode parentName="li">{`consumer_report_permissible_purpose`}</inlineCode></li>
        </ul>
      </li>
      <li parentName="ol">{`(Optional) Unlike Assets, by default, Plaid Check includes only one linked Item per Report. To include multiple Items in a single Consumer Report, set `}<inlineCode parentName="li">{`enable_multi_item_link`}</inlineCode>{` to `}<inlineCode parentName="li">{`true`}</inlineCode>{` in the `}<inlineCode parentName="li">{`/link/token/create`}</inlineCode>{` request.`}</li>
    </ol>
    <LinkTokenCreate useCases={[{
      useCase: 'Consumer Report for credit and lending (Consumer Report by Plaid Check)',
      comment: 'Generate the user_id by calling /user/create',
      config: {
        user: {
          client_user_id: 'user-abc',
          email_address: 'user@example.com'
        },
        user_id: 'usr_9nSp2KuZ2x4JDw',
        products: ['cra_base_report'],
        consumer_report_permissible_purpose: 'EXTENSION_OF_CREDIT',
        enable_multi_item_link: true,
        cra_options: {
          days_requested: 365
        },
        client_name: 'The Loan Arranger',
        language: 'en',
        country_codes: ['US'],
        webhook: 'https://sample-web-hook.com'
      }
    }]} filenameAsTitle={true} mdxType="LinkTokenCreate" />
    <p>{`For more details, see the Plaid Check `}<a parentName="p" {...{
        "href": "/docs/check/add-to-app/"
      }}>{`Implementation guide`}</a>{`.`}</p>
    <h5 {...{
      "id": "adding-consumer-report-to-existing-items"
    }}>{`Adding Consumer Report to existing Items`}</h5>
    <p>{`To enable an existing Assets-enabled Item for Consumer Report, call `}<inlineCode parentName="p">{`/user/create`}</inlineCode>{` and `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{` as described above, but include the Item's `}<inlineCode parentName="p">{`access_token`}</inlineCode>{` when calling `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{`. When Link is launched, the end user will go through the Consumer Report consent flow, and on successful completion of the flow, the Item will be enabled for Consumer Report.`}</p>
    <h4 {...{
      "id": "changes-to-post-link-integration"
    }}>{`Changes to post-Link integration`}</h4>
    <p>{`Update your webhook listeners to listen for the new `}<inlineCode parentName="p">{`USER_CHECK_REPORT_READY`}</inlineCode>{` and `}<inlineCode parentName="p">{`USER_CHECK_REPORT_FAILED`}</inlineCode>{` webhooks.`}</p>
    <ul>
      <li parentName="ul">{`Upon receiving a `}<inlineCode parentName="li">{`USER_CHECK_REPORT_READY`}</inlineCode>{` webhook, you should call `}<inlineCode parentName="li">{`/cra/check_report/base_report/get`}</inlineCode>{`, along with any other Consumer Report product endpoints that you would like to use.`}</li>
      <li parentName="ul">{`Upon receiving a `}<inlineCode parentName="li">{`USER_CHECK_REPORT_FAILED`}</inlineCode>{` webhook, you should call `}<inlineCode parentName="li">{`/user/items/get`}</inlineCode>{` to determine why Items are in a bad state. If appropriate, send the user through `}<a parentName="li" {...{
          "href": "/docs/link/update-mode/#using-update-mode"
        }}>{`Update Mode`}</a>{` to repair them.`}</li>
    </ul>
    <p>{`If you plan to continue using other Plaid Inc. products, such as `}<a parentName="p" {...{
        "href": "https://plaid.com/products/auth/"
      }}>{`Auth`}</a>{` or `}<a parentName="p" {...{
        "href": "https://plaid.com/products/balance/"
      }}>{`Balance`}</a>{`, you should continue to retrieve the public token and exchange it for an access token. If not, you no longer need to obtain an access token.`}</p>
    <h4 {...{
      "id": "mapping-api-responses"
    }}>{`Mapping API responses`}</h4>
    <p>{`Asset Reports and Base Reports have similar but not identical schemas. This `}<a parentName="p" {...{
        "href": "[redacted]##########################################################################################"
      }}>{`Google Sheet`}</a>{` highlights their differences.`}</p>
    <p>{`If you are using other Plaid Inc. products, note that the `}<inlineCode parentName="p">{`account_id`}</inlineCode>{` returned in API responses from endpoints prefixed with `}<inlineCode parentName="p">{`/cra/`}</inlineCode>{` will not match the `}<inlineCode parentName="p">{`account_id`}</inlineCode>{` returned in responses from non-CRA Plaid endpoints.`}</p>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "prerequisites",
        level: 4,
        title: "Prerequisites",
      },
{
        id: "changes-to-plaid-link-initialization",
        level: 4,
        title: "Changes to Plaid Link initialization",
      },
{
        id: "changes-to-post-link-integration",
        level: 4,
        title: "Changes to post-Link integration",
      },
{
        id: "mapping-api-responses",
        level: 4,
        title: "Mapping API responses",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
